/**
 * <s-quick-view-product> — the product inside the quick view window (templates/product.quick-view.liquid).
 * Resolves the selected options to a variant from the embedded JSON, then updates the price, stock
 * pill, add / Buy it now buttons, the form's variant id, the "full details" link and the image, and
 * dims option values with no available variant. Adding dispatches quick-view:add for <s-quick-view>.
 *
 * Commands (commandfor = this element's id):
 *   --prev, --next, --go (data-qv-index thumbnail) (images), --add, --add-pair (data-variant-id), --qty-down, --qty-up, --pairs-prev, --pairs-next
 * Markup: [data-qv-variants] JSON, [data-qv-option-group] fieldsets with [data-qv-option] radios,
 * template[data-qv-price="<id>"], [data-qv-price], [data-qv-add] (+ [data-qv-add-label], [data-qv-add-price]),
 * [data-qv-buy], [data-qv-id], [data-qv-qty], [data-qv-stock] (+ [data-qv-stock-label]), [data-qv-error],
 * [data-qv-link], [data-qv-track] with [data-qv-media="<media id>"] slides, [data-qv-prev] / [data-qv-next], [data-qv-pairs].
 */
class QuickViewProduct extends HTMLElement {
  #ac;
  #variants = [];
  #frame = 0;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-qv-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.matches('[data-qv-option]')) this.#update(true);
      if (event.target instanceof HTMLInputElement && event.target.matches('[data-qv-qty]')) this.#qty(0);
    }, opts);

    this.addEventListener('command', (event) => {
      const source = event.source instanceof HTMLElement ? event.source : null;
      switch (event.command) {
        case '--prev': this.#step(-1); break;
        case '--next': this.#step(1); break;
        case '--go': this.#go(Number(source?.dataset.qvIndex) || 0); break;
        case '--add': this.#add(source); break;
        case '--add-pair': this.#addPair(source); break;
        case '--qty-down': this.#qty(-1); break;
        case '--qty-up': this.#qty(1); break;
        case '--pairs-prev': this.#pairs(-1); break;
        case '--pairs-next': this.#pairs(1); break;
      }
    }, opts);

    const track = this.querySelector('[data-qv-track]');
    track?.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#syncArrows());
    }, { ...opts, passive: true });

    this.#update(false);
    this.#syncArrows();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #groups() {
    return [...this.querySelectorAll('[data-qv-option-group]')];
  }

  #selection() {
    return this.#groups().map((group) => group.querySelector('[data-qv-option]:checked')?.value ?? null);
  }

  #match(selection) {
    return this.#variants.find((variant) => variant.options.every((value, index) => value === selection[index]));
  }

  #update(fromUser) {
    const selection = this.#selection();
    const variant = this.#groups().length ? this.#match(selection) : this.#variants[0];

    // Selected value labels, plus availability for each value given the other selections.
    this.#groups().forEach((group, index) => {
      const label = group.querySelector('[data-qv-selected]');
      if (label) label.textContent = selection[index] ?? '';
      group.querySelectorAll('[data-qv-option]').forEach((input) => {
        const test = [...selection];
        test[index] = input.value;
        const candidate = this.#match(test);
        input.toggleAttribute('data-unavailable', !candidate || !candidate.available);
      });
    });

    const button = this.querySelector('[data-qv-add]');
    const label = button?.querySelector('[data-qv-add-label]');
    const buy = this.querySelector('[data-qv-buy]');
    const stock = this.querySelector('[data-qv-stock]');
    if (!button || !label) return;

    if (!variant) {
      button.disabled = true;
      if (buy) buy.disabled = true;
      delete button.dataset.variantId;
      label.textContent = this.dataset.labelUnavailable;
      if (stock) stock.dataset.stock = 'out';
      return;
    }

    button.dataset.variantId = String(variant.id);
    button.disabled = !variant.available;
    if (buy) buy.disabled = !variant.available;
    label.textContent = variant.available ? this.dataset.labelAdd : this.dataset.labelSoldOut;
    const addPrice = button.querySelector('[data-qv-add-price]');
    if (addPrice && variant.price) addPrice.textContent = variant.price;

    const id = this.querySelector('[data-qv-id]');
    if (id) id.value = String(variant.id);

    if (stock) {
      stock.dataset.stock = variant.stock || (variant.available ? 'in' : 'out');
      const stockLabel = stock.querySelector('[data-qv-stock-label]');
      if (stockLabel && variant.stockLabel) stockLabel.textContent = variant.stockLabel;
    }

    const template = this.querySelector(`template[data-qv-price="${variant.id}"]`);
    const price = this.querySelector('[data-qv-price]');
    if (template && price) price.replaceChildren(template.content.cloneNode(true));

    this.querySelectorAll('[data-qv-link]').forEach((link) => { link.href = variant.url; });

    if (fromUser && variant.media) {
      const slides = [...this.querySelectorAll('[data-qv-media]')];
      const index = slides.findIndex((slide) => slide.dataset.qvMedia === String(variant.media));
      if (index >= 0) this.#go(index);
    }
  }

  #go(index) {
    const track = this.querySelector('[data-qv-track]');
    if (!track) return;
    const rtl = getComputedStyle(track).direction === 'rtl';
    track.scrollTo({ left: index * track.clientWidth * (rtl ? -1 : 1), behavior: this.#reduceMotion ? 'auto' : 'smooth' });
  }

  #index() {
    const track = this.querySelector('[data-qv-track]');
    if (!track || !track.clientWidth) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  #step(direction) {
    const count = this.querySelectorAll('[data-qv-media]').length;
    const next = Math.min(count - 1, Math.max(0, this.#index() + direction));
    this.#go(next);
  }

  // Hide the arrow at either end of the carousel.
  #syncArrows() {
    const count = this.querySelectorAll('[data-qv-media]').length;
    const index = this.#index();
    const prev = this.querySelector('[data-qv-prev]');
    const next = this.querySelector('[data-qv-next]');
    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;
    this.querySelectorAll('[data-qv-index]').forEach((thumb) => {
      const current = Number(thumb.dataset.qvIndex) === index;
      thumb.toggleAttribute('aria-current', current);
      if (current) thumb.setAttribute('aria-current', 'true');
    });
  }

  #qty(delta) {
    const input = this.querySelector('[data-qv-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    const next = Math.max(1, (parseInt(input.value, 10) || 1) + delta);
    input.value = String(next);
  }

  #pairs(direction) {
    const list = this.querySelector('[data-qv-pairs]');
    if (!list) return;
    const rtl = getComputedStyle(list).direction === 'rtl';
    list.scrollBy({ left: direction * list.clientWidth * (rtl ? -1 : 1), behavior: this.#reduceMotion ? 'auto' : 'smooth' });
  }

  #dispatchAdd(button, quantity) {
    this.dispatchEvent(new CustomEvent('quick-view:add', {
      bubbles: true,
      detail: { id: button.dataset.variantId, quantity, button, error: this.querySelector('[data-qv-error]') },
    }));
  }

  #add(button) {
    if (!(button instanceof HTMLButtonElement) || button.disabled || !button.dataset.variantId) return;
    const input = this.querySelector('[data-qv-qty]');
    const quantity = Math.max(1, parseInt(input?.value, 10) || 1);
    this.#dispatchAdd(button, quantity);
  }

  #addPair(button) {
    if (!(button instanceof HTMLButtonElement) || !button.dataset.variantId) return;
    this.#dispatchAdd(button, 1);
  }
}

if (!customElements.get('s-quick-view-product')) customElements.define('s-quick-view-product', QuickViewProduct);
